import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { authAPI } from '../services/apiClient';
import { secureClearAll } from '../utils/secureStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Seed user display info from localStorage (non-sensitive: id, name, email).
  // The actual access token lives only in memory (_accessToken in apiClient.js).
  const [user, setUser] = useState(() => authAPI.getUser());
  const [authReady, setAuthReady] = useState(false);

  // Listen for session expiry events dispatched by apiClient when a token refresh fails.
  useEffect(() => {
    const onExpired = () => {
      authAPI.logout().catch(() => {});
      setUser(null);
      sessionStorage.setItem('airfly_session_expired', '1');
    };
    window.addEventListener('airfly:session-expired', onExpired);
    return () => window.removeEventListener('airfly:session-expired', onExpired);
  }, []);

  // On mount, attempt a silent token refresh using the HttpOnly refresh cookie.
  // If the cookie is present and valid, we get a new access token without the user
  // having to log in again after a page reload.
  useEffect(() => {
    Promise.resolve(authAPI.silentRefresh()).then((refreshed) => {
      if (refreshed) {
        setUser(authAPI.getUser());
      } else {
        // No valid session — clear any stale user display info.
        setUser(null);
      }
    }).finally(() => {
      setAuthReady(true);
    });
  }, []);

  const login = async (email, password, rememberMe = false) => {
    const data = await authAPI.login(email, password, rememberMe);
    setUser(data.user || authAPI.getUser());
    return data;
  };

  const logout = async () => {
    await authAPI.logout();
    setUser(null);
    
    // Security: Clear any booking preview data on logout (shared computer safety)
    try {
      sessionStorage.removeItem('airfly_checkout_preview');
      secureClearAll(); // Also clear any encrypted storage items
    } catch {
      // Ignore storage errors
    }
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      authReady,
      login,
      logout,
      setUser,
    }),
    [user, authReady]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
